import { ArrowUpRight, CheckCircle2, Languages, Microscope, ShieldCheck, Stethoscope } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHero } from '../components/PageHero'
import { PageMeta } from '../components/PageMeta'
import { Reveal } from '../components/Reveal'
import { SectionHeading } from '../components/SectionHeading'
import { doctorProfile } from '../data/site'

const timeline = [
  {
    period: '2024 – 2025',
    role: 'General Doctor',
    place: 'Primary and outpatient care',
    copy: 'Patient assessment, everyday clinical care, diagnostics, follow-up, and clear health conversations.',
  },
  {
    period: '2022 – 2023',
    role: 'Resident Medical Officer',
    place: 'Critical care',
    copy: 'ICU and ward care, physiological monitoring, multidisciplinary rounds, and clinical collaboration.',
  },
  {
    period: '2016 – 2022',
    role: 'MBBS',
    place: 'Government Medical College Ernakulam',
    copy: 'Medical education and rotating internship through Kerala University of Health Sciences.',
  },
]

export default function AboutPage() {
  return (
    <>
      <PageMeta
        title="About"
        description="Learn about Dr. Fathima Finu’s clinical background, approach to general medicine, and interest in safer, clearer health technology."
        image="/images/dr-fathima-finu.jpg"
        imageAlt="Professional portrait of Dr. Fathima Finu"
      />
      <PageHero
        eyebrow="About Dr. Fathima"
        title={<>A doctor who values the details—and the <span className="serif-accent">person around them.</span></>}
        copy="My experience across general medicine and critical care shapes an approach that is careful, collaborative, and grounded in explanations people can use."
        tone="coral"
        aside={
          <div className="hero-quote-card">
            <span className="quote-mark">“</span>
            <p>Listening is not the warm-up to medical care. It is part of the clinical work.</p>
          </div>
        }
      />

      <section className="section about-intro-section">
        <div className="container about-intro-grid">
          <Reveal className="about-portrait-card">
            <div className="portrait-photo">
              <img
                src="/images/dr-fathima-finu.jpg"
                srcSet="/images/dr-fathima-finu-640.jpg 640w, /images/dr-fathima-finu.jpg 1000w"
                sizes="(max-width: 860px) min(100vw - 32px, 450px), 430px"
                width="1000"
                height="1000"
                alt="Professional portrait of Dr. Fathima Finu"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="portrait-caption">
              <span className="eyebrow">General doctor</span>
              <strong>{doctorProfile.name}</strong>
              <span>Malayalam · English · Hindi</span>
            </div>
          </Reveal>
          <Reveal className="about-long-copy" delay={0.08}>
            <span className="eyebrow">My approach</span>
            <h2>Care should be rigorous enough for the science and clear enough for real life.</h2>
            <p>{doctorProfile.summary}</p>
            <p>I have worked with patients in outpatient clinics, inpatient wards, and critical-care settings. That range sharpened my attention to patient safety, changing clinical signs, multidisciplinary communication, and the value of a plan that everyone understands.</p>
            <p>This website extends that philosophy beyond the consultation room. It brings together evidence-based explainers, practical Android tools in development, emerging-research updates, and a privacy-minded place to suggest questions that matter to patients.</p>
            <div className="about-links">
              <a className="button" href={doctorProfile.linkedin} target="_blank" rel="noreferrer"><span className="linkedin-glyph" aria-hidden="true">in</span> LinkedIn profile <ArrowUpRight size={16} /></a>
              <Link className="button button-ghost" to="/booking">Request a consultation</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section principles-section">
        <div className="container">
          <Reveal><SectionHeading eyebrow="What guides the work" title={<>Four principles, <span className="serif-accent">one standard of care.</span></>} /></Reveal>
          <div className="principles-grid">
            {[
              { icon: Stethoscope, title: 'Start clinical', copy: 'Listen carefully, examine thoughtfully, and keep the differential grounded in the information available.' },
              { icon: CheckCircle2, title: 'Make it understandable', copy: 'Translate the reasoning, options, and warning signs into language that supports informed decisions.' },
              { icon: ShieldCheck, title: 'Protect what is personal', copy: 'Collect only what is needed, keep boundaries explicit, and never turn private stories into public content.' },
              { icon: Microscope, title: 'Stay curious', copy: 'Follow new evidence with optimism and discipline—especially when early findings are more exciting than certain.' },
            ].map((item, index) => (
              <Reveal className="principle-card" delay={index * 0.06} key={item.title}>
                <item.icon size={25} />
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section timeline-section">
        <div className="container timeline-grid">
          <Reveal className="timeline-heading">
            <span className="eyebrow">Training & experience</span>
            <h2>A foundation across the clinic, ward, and ICU.</h2>
            <div className="language-pill"><Languages size={19} /> Consultations can be supported in three languages.</div>
          </Reveal>
          <div className="timeline-list">
            {timeline.map((item, index) => (
              <Reveal className="timeline-item" key={item.role} delay={index * 0.06}>
                <span className="timeline-dot" />
                <span className="timeline-period">{item.period}</span>
                <div><h3>{item.role}</h3><strong>{item.place}</strong><p>{item.copy}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section interests-section">
        <div className="container interest-panel">
          <Reveal>
            <span className="eyebrow">Looking forward</span>
            <h2>Where medicine meets thoughtful technology.</h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p>I’m especially interested in tools that make care safer, less invasive, and easier to understand—from point-of-care diagnostics and physiological monitoring to practical digital health.</p>
            <div className="tag-list">{doctorProfile.interests.map((interest) => <span key={interest}>{interest}</span>)}</div>
          </Reveal>
        </div>
      </section>
    </>
  )
}
